import { F0AiTableCard, F0CanvasCard, type DataDownloadDataset } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { useAutoOpenCanvas } from 'modules/ai/copilotActions/canvas/hooks/useAutoOpenCanvas'

/**
 * Below this row count we render the dataset inline as a simple table
 * instead of opening the canvas. The threshold is exclusive.
 */
const INLINE_TABLE_MAX_ROWS = 15

export type DataDownloadCardProps = {
  title?: string
  dataset: DataDownloadDataset
  filename?: string
  markdown?: string

  /** Whether this card is currently open in the canvas. */
  isActive: boolean
  /** Open the canvas with this dataset. */
  onOpen: () => void
  /** Close the canvas. */
  onClose: () => void
  /**
   * Optional key for one-shot auto-open. Defaults to the autoOpenKey prop;
   * omit when rendering inline.
   */
  autoOpenKey?: string
}

export function DataDownloadCard({
  title: titleProp,
  dataset,
  filename,
  markdown: _markdown,
  isActive,
  onOpen,
  onClose,
  autoOpenKey,
}: DataDownloadCardProps) {
  const cleanFilename = filename?.replaceAll('_', ' ')

  const title = titleProp || cleanFilename || i18n.t('common.ai.data_download.title')

  // Render inline only when the rows we have represent the complete
  // dataset and it's small enough to display in the chat thread.
  const hasFullDataset = dataset.totalCount == null || dataset.totalCount <= dataset.rows.length
  const renderInline = hasFullDataset && dataset.rows.length < INLINE_TABLE_MAX_ROWS

  // Skip auto-opening when we render inline.
  useAutoOpenCanvas(renderInline ? undefined : autoOpenKey, onOpen)

  if (renderInline) {
    return <F0AiTableCard dataset={dataset} title={title} />
  }

  return (
    <F0CanvasCard
      module="analytics"
      title={title}
      description={
        dataset.totalCount != null
          ? i18n.t('common.ai.data_download.rows', { amount: dataset.totalCount })
          : i18n.t('common.ai.data_download.title')
      }
      onOpen={onOpen}
      onClose={onClose}
      isActive={isActive}
    />
  )
}
